import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HeadphonesIcon, StarIcon, CheckCircleIcon, ChevronRightIcon, ChevronLeftIcon, MicIcon, PlayIcon, PauseIcon, ArrowRightIcon, UsersIcon, FilterIcon } from 'lucide-react';
import { audiobooks } from '../AudioBookDetails';
import CustomPromotionalBannerCarousel from '../components/UI/CustomPromotionalBannerCarousel';
import AudiobookCard from '../components/Books/AudiobookCard';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
// Genre Tile Data
const genreTiles = [{
  name: 'Romance',
  count: '1.2k+',
  image: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=400',
  slug: 'romance',
  color: 'from-pink-600 to-rose-600'
}, {
  name: 'Fantasy',
  count: '850+',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400',
  slug: 'fantasy',
  color: 'from-indigo-600 to-violet-600'
}, {
  name: 'Sci-Fi',
  count: '600+',
  image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400',
  slug: 'sci-fi',
  color: 'from-blue-600 to-cyan-600'
}, {
  name: 'Mystery',
  count: '900+',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
  slug: 'mystery',
  color: 'from-slate-600 to-gray-600'
}, {
  name: 'Thriller',
  count: '750+',
  image: 'https://images.unsplash.com/photo-1509281373149-e957c6296406?w=400',
  slug: 'thriller',
  color: 'from-red-600 to-orange-600'
}, {
  name: 'Horror',
  count: '400+',
  image: 'https://images.unsplash.com/photo-1509557965875-b88c97052f0e?w=400',
  slug: 'horror',
  color: 'from-gray-900 to-black'
}, {
  name: 'Historical',
  count: '500+',
  image: 'https://images.unsplash.com/photo-1461360370896-922624d12a74?w=400',
  slug: 'historical-fiction',
  color: 'from-amber-700 to-yellow-600'
}, {
  name: 'Contemporary',
  count: '1.5k+',
  image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
  slug: 'contemporary',
  color: 'from-teal-600 to-emerald-600'
}];
// Mock Featured Narrator Data
const featuredNarrator = {
  id: 'julia-whelan',
  name: 'Julia Whelan',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400',
  bio: 'Award-winning narrator known for bringing complex characters to life with emotional depth. With over 400 audiobooks, Julia is one of the most sought-after voices in contemporary fiction and romance.',
  booksNarrated: '400+',
  rating: 4.9,
  genres: ['Romance', 'Contemporary', 'Literary Fiction']
};
type NarratorFilter = 'all' | 'solo' | 'duet' | 'multi';
const Audiobooks = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [narratorFilter, setNarratorFilter] = useState<NarratorFilter>('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const audioRefs = useRef<{
    [key: string]: HTMLAudioElement;
  }>({});
  // Handle audio playback
  const handlePlayPause = (bookId: string) => {
    if (currentlyPlaying === bookId) {
      // Pause the currently playing audio
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].pause();
      }
      setCurrentlyPlaying(null);
    } else {
      // Pause any currently playing audio
      if (currentlyPlaying && audioRefs.current[currentlyPlaying]) {
        audioRefs.current[currentlyPlaying].pause();
      }
      // Play the new audio
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].play();
        setCurrentlyPlaying(bookId);
      }
    }
  };
  // Handle audio ended event
  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };
  // Scroll handler for carousels
  const scrollCarousel = (id: string, direction: 'left' | 'right') => {
    const container = document.getElementById(id);
    if (container) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  // Filter function
  const filterBooks = (books: typeof audiobooks) => {
    if (narratorFilter === 'all') return books;
    return books.filter(book => {
      const count = book.narrators.length;
      if (narratorFilter === 'solo') return count === 1;
      if (narratorFilter === 'duet') return count === 2;
      if (narratorFilter === 'multi') return count > 2;
      return true;
    });
  };
  // Filtered lists
  const filteredAllBooks = filterBooks(audiobooks);
  const romanceBooks = filterBooks(audiobooks.filter(b => b.genre === 'Romance' || b.subgenres && b.subgenres.includes('Romance')));
  const fantasySciFiBooks = filterBooks(audiobooks.filter(b => ['Fantasy', 'Science Fiction'].includes(b.genre) || b.subgenres && b.subgenres.some(sg => ['Fantasy', 'Science Fiction'].includes(sg))));
  // Mock staff picks (just a mix for demo) - filtered
  const staffPicks = filterBooks([...audiobooks].sort(() => 0.5 - Math.random()).slice(0, 6));
  // Mock narrator books (just taking some romance books for demo)
  const narratorBooks = romanceBooks.slice(0, 6);
  const filterSections: FilterSection[] = [{
    id: 'narration',
    title: 'Narration Type',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Narrations'
    }, {
      value: 'solo',
      label: 'Solo Narration'
    }, {
      value: 'duet',
      label: 'Duet Narration'
    }, {
      value: 'multi',
      label: 'Multi-Cast'
    }],
    value: narratorFilter,
    onChange: val => setNarratorFilter(val as NarratorFilter)
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-6523" data-mp="3tgt7987d1yjg">
      {/* 1. Banner */}
      <CustomPromotionalBannerCarousel data-id="element-6524" data-mp="bzighrmazflm" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-6525" data-mp="c0bcpynmympi">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-6526" data-mp="76z1qrbvqef8">
          <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.card} ${dk.textSecondary} hover:${dk.surfaceMuted}`} data-id="element-6527" data-mp="kz5hicb4eje5">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-6528" data-mp="uxcmdws4xazs" />
            <span data-id="element-6529" data-mp="py8c12th577v">Filters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-6530" data-mp="pwos928m0e1c">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-6531" data-mp="2wrbte5y8566o">
            <FilterSidebar title="Filters" sections={filterSections} onReset={() => setNarratorFilter('all')} searchable={false} data-id="element-6532" data-mp="1c7owurtkloo4" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-6533" data-mp="12f0y3rj15amr">
            {/* 2. Genre Quick-Links */}
            <section className={`py-8 mb-8 rounded-xl shadow-sm ${dk.card}`} data-id="element-6534" data-mp="ojp4xokivs7y">
              <div className="px-6" data-id="element-6535" data-mp="skr6ic1wwgd3">
                <div className="flex items-center mb-6" data-id="element-6536" data-mp="sgv19oc2yuor">
                  <HeadphonesIcon className={`h-6 w-6 mr-3 ${rt.textPrimary}`} data-id="element-6537" data-mp="ekq2wou9iutx" />
                  <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6538" data-mp="jirl77jv6q4c">
                    Browse by Genre
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-6539" data-mp="2dwsllwv2bl6j">
                  {genreTiles.map(genre => <Link key={genre.name} to={`/audiobooks/${genre.slug}`} className="group relative h-24 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" data-id="element-6540" data-mp="10oeav8l3slxe">
                      <div className="absolute inset-0" data-id="element-6541" data-mp="mxrjk1ho9ybc">
                        <img src={genre.image} alt={genre.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-6542" data-mp="hzpsata3esos" />
                        <div className={`absolute inset-0 bg-gradient-to-br ${genre.color} opacity-70 group-hover:opacity-60 transition-opacity`} data-id="element-6543" data-mp="90m9zrir2hbc"></div>
                      </div>
                      <div className="absolute inset-0 p-3 flex flex-col justify-end" data-id="element-6544" data-mp="1ycwq5gkxch08">
                        <h3 className="text-white font-bold text-sm" data-id="element-6545" data-mp="25wz1lxhg2h13">
                          {genre.name}
                        </h3>
                        <p className="text-white/80 text-[10px] font-medium" data-id="element-6546" data-mp="2av0jif34waaz">
                          {genre.count}
                        </p>
                      </div>
                    </Link>)}
                </div>
              </div>
            </section>

            {/* 3. New Audiobook Releases */}
            <section className="mb-12" data-id="element-6547" data-mp="1mzxqduxarkop">
              <div className="flex items-center justify-between mb-6" data-id="element-6548" data-mp="1rwy0qi16j9lm">
                <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6549" data-mp="1rz0y5n4nw50d">
                  New Releases
                </h2>
                <Link to="/audiobooks/new" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-6550" data-mp="20xmercqkp9yi">
                  See More <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6551" data-mp="38dse30kmfjss" />
                </Link>
              </div>

              {filteredAllBooks.length > 0 ? <div className="relative group/carousel" data-id="element-6552" data-mp="2cz70fwjkpiw0">
                  <button onClick={() => scrollCarousel('new-releases-carousel', 'left')} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} aria-label="Scroll left" data-id="element-6553" data-mp="2hzbivs41knov">
                    <ChevronLeftIcon className="h-5 w-5" data-id="element-6554" data-mp="1z1iioxksyrv6" />
                  </button>
                  <div id="new-releases-carousel" className="flex overflow-x-auto pb-4 hide-scrollbar gap-6 px-1 snap-x" data-id="element-6555" data-mp="23z0r8muwr52n">
                    {filteredAllBooks.map(book => <div key={book.title} className="flex-none snap-start w-56" data-id="element-6556" data-mp="3fe7f89u47bru">
                        <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-6557" data-mp="18qyisoahgier" />
                        <audio ref={el => {
                    if (el) audioRefs.current[book.title] = el;
                  }} src={book.audioSampleUrl} onEnded={handleAudioEnded} className="hidden" data-id="element-6558" data-mp="3pyrwfot8wksh" />
                      </div>)}
                  </div>
                  <button onClick={() => scrollCarousel('new-releases-carousel', 'right')} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} aria-label="Scroll right" data-id="element-6559" data-mp="ckdej77xejle">
                    <ChevronRightIcon className="h-5 w-5" data-id="element-6560" data-mp="1k9ziurlu6gcz" />
                  </button>
                </div> : <div className={`text-center py-12 ${dk.textMuted}`} data-id="element-6561" data-mp="1fetzo0jtjfsl">
                  No audiobooks found with {narratorFilter} narration.
                </div>}
            </section>

            {/* 4. Narrator Spotlight */}
            <section className={`mb-12 rounded-xl overflow-hidden shadow-sm ${dk.card}`} data-id="element-6562" data-mp="11k9rkeha8cpt">
              <div className="p-6 md:p-8" data-id="element-6563" data-mp="wn9j5wwymxvj">
                <div className="flex items-center justify-between mb-8" data-id="element-6564" data-mp="wk58cgozp3vw">
                  <div className="flex items-center" data-id="element-6565" data-mp="1aeo2k4us5134">
                    <MicIcon className="h-6 w-6 mr-3 text-amber-500" data-id="element-6566" data-mp="15m4t9k34ks44" />
                    <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6567" data-mp="hx2b8b4kwx93">
                      Narrator Spotlight
                    </h2>
                  </div>
                  <Link to="/narrators" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-6568" data-mp="duyvhaavn6i0">
                    View All Narrators{' '}
                    <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-6569" data-mp="vhpymhlfqzc8" />
                  </Link>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 items-center" data-id="element-6570" data-mp="10h2kaz0km0d8">
                  {/* Narrator Profile - Condensed */}
                  <div className="lg:w-1/3 flex flex-col items-center text-center lg:items-start lg:text-left w-full" data-id="element-6571" data-mp="1a98ca8qtohsf">
                    <div className="flex items-center gap-4 mb-4" data-id="element-6572" data-mp="1e98xqdcc9t8z">
                      <div className="relative" data-id="element-6573" data-mp="1j9n0bfv68azm">
                        <img src={featuredNarrator.avatar} alt={featuredNarrator.name} className="w-20 h-20 rounded-full object-cover border-4 border-indigo-100 shadow-sm" data-id="element-6574" data-mp="1j9wt1b7low90" />
                        <div className="absolute bottom-0 right-0 bg-amber-400 text-white p-1 rounded-full shadow-sm" data-id="element-6575" data-mp="3q0w4jquw5m4w">
                          <StarIcon className="h-3 w-3 fill-current" data-id="element-6576" data-mp="3ihv9lty5p0t5" />
                        </div>
                      </div>
                      <div className="text-left" data-id="element-6577" data-mp="3dnr0p55byipf">
                        <h3 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6578" data-mp="38rk5o7eh2rm9">
                          {featuredNarrator.name}
                        </h3>
                        <div className="flex items-center gap-2 text-xs mt-1" data-id="element-6579" data-mp="59s5w6u3h3di">
                          <span className={`${rt.textPrimary} font-bold`} data-id="element-6580" data-mp="57phtsci5kbf">
                            {featuredNarrator.booksNarrated} Books
                          </span>
                          <span className={dk.textMuted} data-id="element-6581" data-mp="3rffcf7bo355v">•</span>
                          <span className="text-amber-500 font-bold" data-id="element-6582" data-mp="3mif9v6e10kji">
                            {featuredNarrator.rating} Rating
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 justify-center lg:justify-start mb-3" data-id="element-6583" data-mp="2b1fh69nro3oy">
                      {featuredNarrator.genres.map(g => <span key={g} className={`text-[10px] px-2 py-0.5 ${rt.badgeRadius} ${dk.badgeNeutral}`} data-id="element-6584" data-mp="1756eu5dus73b">
                          {g}
                        </span>)}
                    </div>

                    <p className={`text-sm mb-5 leading-relaxed line-clamp-2 ${dk.textSecondary}`} data-id="element-6585" data-mp="je9c9j7y6m4q">
                      {featuredNarrator.bio}
                    </p>

                    <Link to={`/narrators/${featuredNarrator.id}`} className={`w-full py-2 rounded-lg font-medium transition-colors text-center text-sm ${rt.primaryButton}`} data-id="element-6586" data-mp="afxb4n9gg5y1">
                      View Profile
                    </Link>
                  </div>

                  {/* Narrator's Books - Carousel */}
                  <div className="lg:w-2/3 w-full relative group/carousel" data-id="element-6587" data-mp="3hydh8bz64s5r">
                    <button onClick={() => scrollCarousel('narrator-carousel', 'left')} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} aria-label="Scroll left" data-id="element-6588" data-mp="3dxthr1r4hcgg">
                      <ChevronLeftIcon className="h-5 w-5" data-id="element-6589" data-mp="q8igyjvmzjt" />
                    </button>

                    <div id="narrator-carousel" className="flex overflow-x-auto pb-4 hide-scrollbar gap-6 px-1 snap-x" data-id="element-6590" data-mp="ehdg5v3ophri">
                      {narratorBooks.map(book => <div key={book.title} className="flex-none snap-start w-48" data-id="element-6591" data-mp="oe8b640r1011">
                          <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-6592" data-mp="309rx9up94gtu" />
                        </div>)}
                    </div>

                    <button onClick={() => scrollCarousel('narrator-carousel', 'right')} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} aria-label="Scroll right" data-id="element-6593" data-mp="6wzy0bze1c6m">
                      <ChevronRightIcon className="h-5 w-5" data-id="element-6594" data-mp="3fdl67qk7418o" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* 5. Popular in Romance */}
            <section className="mb-12" data-id="element-6595" data-mp="396onql10x43c">
              <div className="flex items-center justify-between mb-6" data-id="element-6596" data-mp="395n3bpy4b58y">
                <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6597" data-mp="3483oantliofy">
                  Popular in Romance
                </h2>
                <Link to="/audiobooks/romance" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-6598" data-mp="2z65xn8iv8rcz">
                  See All Romance <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6599" data-mp="2q82l31puz4xb" />
                </Link>
              </div>

              {romanceBooks.length > 0 ? <div className="relative group/carousel" data-id="element-6600" data-mp="2gh80s2w6afei">
                  <button onClick={() => scrollCarousel('romance-carousel', 'left')} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-6601" data-mp="2ghr7engdhhd8">
                    <ChevronLeftIcon className="h-5 w-5" data-id="element-6602" data-mp="hzxsz2n60agn" />
                  </button>
                  <div id="romance-carousel" className="flex overflow-x-auto pb-4 hide-scrollbar gap-6 px-1 snap-x" data-id="element-6603" data-mp="i20kjr3yubnh">
                    {romanceBooks.map(book => <div key={book.title} className="flex-none snap-start w-56" data-id="element-6604" data-mp="vzyr4ac1p3sf">
                        <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-6605" data-mp="3vchen9rrix4c" />
                      </div>)}
                  </div>
                  <button onClick={() => scrollCarousel('romance-carousel', 'right')} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-6606" data-mp="8xnh2u27r9q6">
                    <ChevronRightIcon className="h-5 w-5" data-id="element-6607" data-mp="1sst2ub9mg30u" />
                  </button>
                </div> : <div className={`text-center py-8 ${dk.textMuted}`} data-id="element-6608" data-mp="1ivwrlxsc92sl">
                  No romance audiobooks found with {narratorFilter} narration.
                </div>}
            </section>

            {/* 6. Staff Picks */}
            <section className={`py-8 px-6 mb-12 rounded-xl ${dk.surfaceMuted}`} data-id="element-6609" data-mp="2blwwvxqr4hvz">
              <div className="flex items-center justify-between mb-6" data-id="element-6610" data-mp="2gj5gm2rjstzc">
                <div className="flex items-center" data-id="element-6611" data-mp="21vkdi8uoaurf">
                  <StarIcon className="h-6 w-6 mr-3 text-yellow-500 fill-current" data-id="element-6612" data-mp="21sodmez9lw0c" />
                  <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6613" data-mp="26s13l8lfnj75">
                    Staff Picks
                  </h2>
                </div>
                <Link to="/audiobooks/staff-picks" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-6614" data-mp="2zaokebtecc2f">
                  See More <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6615" data-mp="16mrzr4rasow1" />
                </Link>
              </div>

              {staffPicks.length > 0 ? <div className="relative group/carousel" data-id="element-6616" data-mp="11ogpisx3ccda">
                  <button onClick={() => scrollCarousel('staff-picks-carousel', 'left')} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-6617" data-mp="woucajh9iw4h">
                    <ChevronLeftIcon className="h-5 w-5" data-id="element-6618" data-mp="1fnganqlea94p" />
                  </button>
                  <div id="staff-picks-carousel" className="flex overflow-x-auto pb-4 hide-scrollbar gap-6 px-1 snap-x" data-id="element-6619" data-mp="1fllzv53u7xec">
                    {staffPicks.map(book => <div key={book.title} className="flex-none snap-start w-56" data-id="element-6620" data-mp="2fns54u3tdbpl">
                        <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-6621" data-mp="2kofuzcgb3mi8" />
                      </div>)}
                  </div>
                  <button onClick={() => scrollCarousel('staff-picks-carousel', 'right')} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-6622" data-mp="25wnieklb30ca">
                    <ChevronRightIcon className="h-5 w-5" data-id="element-6623" data-mp="kjkcnmynp0g0" />
                  </button>
                </div> : <div className={`text-center py-8 ${dk.textMuted}`} data-id="element-6624" data-mp="gjtrpzvt9eje">
                  No staff picks found with {narratorFilter} narration.
                </div>}
            </section>

            {/* 7. Fantasy & Sci-Fi */}
            <section className="mb-12" data-id="element-6625" data-mp="ahzy8rw8gq5e">
              <div className="flex items-center justify-between mb-6" data-id="element-6626" data-mp="3suy1p8s8i14b">
                <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6627" data-mp="1nvawigb5wum">
                  Fantasy & Sci-Fi
                </h2>
                <Link to="/audiobooks/fantasy-scifi" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-6628" data-mp="3ivpagnor70ln">
                  See All <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6629" data-mp="39vtve7s2ulgc" />
                </Link>
              </div>

              {fantasySciFiBooks.length > 0 ? <div className="relative group/carousel" data-id="element-6630" data-mp="3eun83xzji8fg">
                  <button onClick={() => scrollCarousel('fantasy-carousel', 'left')} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-6631" data-mp="183nrj3xe6scp">
                    <ChevronLeftIcon className="h-5 w-5" data-id="element-6632" data-mp="2gqfvpo9wdpje" />
                  </button>
                  <div id="fantasy-carousel" className="flex overflow-x-auto pb-4 hide-scrollbar gap-6 px-1 snap-x" data-id="element-6633" data-mp="2bw3ap9srw65j">
                    {fantasySciFiBooks.map(book => <div key={book.title} className="flex-none snap-start w-56" data-id="element-6634" data-mp="1xxv5yfphw0jt">
                        <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-6635" data-mp="1szjww770xu2n" />
                      </div>)}
                  </div>
                  <button onClick={() => scrollCarousel('fantasy-carousel', 'right')} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-opacity ${dk.card} hover:${dk.surfaceMuted}`} data-id="element-6636" data-mp="2bjix83oex1br">
                    <ChevronRightIcon className="h-5 w-5" data-id="element-6637" data-mp="2supo9yhprukr" />
                  </button>
                </div> : <div className={`text-center py-8 ${dk.textMuted}`} data-id="element-6638" data-mp="2xsitopb70kjz">
                  No fantasy/sci-fi audiobooks found with {narratorFilter}{' '}
                  narration.
                </div>}
            </section>

            {/* 8. Lifestyle CTA */}
            <section className="relative py-24 rounded-xl overflow-hidden mb-12" data-id="element-6639" data-mp="29xy7pwtk3tke">
              <div className="absolute inset-0" data-id="element-6640" data-mp="2euy75ovwhy2j">
                <img src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1200" alt="Listening lifestyle" className="w-full h-full object-cover" data-id="element-6641" data-mp="2exkmwbsy3jvu" />
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/90 to-indigo-900/80" data-id="element-6642" data-mp="1xlvtzgthuojs"></div>
              </div>
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-6643" data-mp="1sona9bsp6cgf">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-6644" data-mp="27d9yy447sgkb">
                  Listen Anywhere, Anytime
                </h2>
                <p className="text-lg text-indigo-100 max-w-2xl mx-auto mb-8" data-id="element-6645" data-mp="22jnli9q888ng">
                  From your morning commute to late-night listening sessions,
                  our audiobooks are with you wherever you go. Experience
                  stories brought to life.
                </p>
                <Link to="/audiobooks/browse" className="inline-flex items-center px-8 py-3 rounded-full bg-white text-indigo-900 font-bold hover:bg-indigo-50 transition-colors shadow-lg" data-id="element-6646" data-mp="2q902hvrspl0b">
                  Browse All Audiobooks
                </Link>
              </div>
            </section>

            {/* 9. Why Choose Our Audiobooks */}
            <section className={`py-16 rounded-xl ${dk.card}`} data-id="element-6647" data-mp="1go74olyc5ctq">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6648" data-mp="1kor3dtyk4ohz">
                <h2 className={`text-2xl font-bold text-center mb-12 ${dk.textHeading}`} data-id="element-6649" data-mp="1pm9cbkckr3pz">
                  Why Choose Our Audiobooks?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-6650" data-mp="1zl89o6rw3cxo">
                  <div className={`p-8 rounded-2xl text-center transition-transform hover:-translate-y-1 ${dk.surfaceMuted}`} data-id="element-6651" data-mp="1zivv83lcbkpd">
                    <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 ${rt.bgMedium}`} data-id="element-6652" data-mp="28el9ztsa9gts">
                      <HeadphonesIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-6653" data-mp="2ibzrzxtuudo7" />
                    </div>
                    <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-6654" data-mp="2na1767o5tsue">
                      Professional Narration
                    </h3>
                    <p className={dk.textSecondary} data-id="element-6655" data-mp="bl4jfl0qglwp">
                      Experience stories brought to life by talented voice
                      actors and award-winning narrators.
                    </p>
                  </div>
                  <div className={`p-8 rounded-2xl text-center transition-transform hover:-translate-y-1 ${dk.surfaceMuted}`} data-id="element-6656" data-mp="3svkshvfmqf8l">
                    <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 ${rt.bgMedium}`} data-id="element-6657" data-mp="ufrwh5uwlpxb">
                      <StarIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-6658" data-mp="kmkuzw3aau2i" />
                    </div>
                    <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-6659" data-mp="fo17hr91la81">
                      Premium Quality
                    </h3>
                    <p className={dk.textSecondary} data-id="element-6660" data-mp="1d61b5f7vna5w">
                      Studio-quality audio production ensures an immersive and
                      crystal-clear listening experience.
                    </p>
                  </div>
                  <div className={`p-8 rounded-2xl text-center transition-transform hover:-translate-y-1 ${dk.surfaceMuted}`} data-id="element-6661" data-mp="xuthlv45guic">
                    <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 ${rt.bgMedium}`} data-id="element-6662" data-mp="nwdq3r17nj8b">
                      <CheckCircleIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-6663" data-mp="1bmgcbqig1uz0" />
                    </div>
                    <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-6664" data-mp="11woiiyi655vl">
                      Multi-Platform Access
                    </h3>
                    <p className={dk.textSecondary} data-id="element-6665" data-mp="16texfii8lvff">
                      Listen seamlessly on any device through our mobile apps
                      and web player, anytime.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <style jsx data-id="element-6666" data-mp="3jzlcwepe3bdd">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
};
export default Audiobooks;